/**
 * Custom hook to fetch and manage Jira CMDB data.
 *
 * @param {Object} loginConfig - The login configuration object.
 * @param {any} loginConfig.id - The login ID used for fetching Jira CMDB data.
 * This hook performs the following:
 * - Initializes the state to store Jira CMDB data.
 * - Defines a `fetchData` function to fetch Jira CMDB data from the API.
 * - Uses `useEffect` to fetch data on component mount and cleanup on unmount.
 * - Returns the Jira CMDB data and the `fetchData` function for manual refetching.
 */
import { isEmpty } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { EmptyCancellableRequest } from "restAPI";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";

import { useCmdbSupportedIntegrations } from "../genericIntegrationTicketUtils";
import { JiraCMDBDataProps } from "./JiraCMDBMappingForm";

export const useJiraCMDBData = ({
  loginConfig,
  toolName,
}: {
  loginConfig: any;
  toolName: string;
  fetchOnMount?: boolean;
}) => {
  const [jiraCMDBData, setJiraCMDBData] = useState<JiraCMDBDataProps[]>([]);
  const cmdbSupportedTools = useCmdbSupportedIntegrations(toolName);
  const fetchData = useCallback(() => {
    if (!loginConfig?.id || isEmpty(cmdbSupportedTools)) {
      setJiraCMDBData([]);
      return EmptyCancellableRequest;
    }
    const { promise, cancel } = ToolsIntegrationsAPI.getJiraCMDBList({
      ticketSystemType: toolName.replace(" ", "_").toUpperCase(),
      loginId: loginConfig.id,
    });
    promise
      .then((data) => {
        if (data.success) {
          setJiraCMDBData(data.data);
        }
      })
      .catch((err) => {
        console.log(err);
      });
    return { promise, cancel };
  }, [cmdbSupportedTools, loginConfig?.id, toolName]);

  useEffect(() => {
    const { cancel } = fetchData();
    return () => {
      if (cancel) {
        cancel(); // Cancel API request on cleanup
      }
    };
  }, [fetchData]);

  return { jiraCMDBData, fetchData };
};
